import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const TripDeleteModal = ({ 
  isOpen, 
  onClose, 
  tripId, 
  tripNumber, 
  navigate,
  onTripDeleted 
}) => {
  const [tripDetails, setTripDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOption, setDeleteOption] = useState('unassign'); // 'unassign' or 'delete'

  // Helper function to get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) {
      toast.error('Authentication required. Please login again.');
      navigate('/login');
      return null;
    }
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  }, [navigate]);

  const fetchTripDetails = useCallback(async () => {
    setLoading(true);
    try {
      const authHeaders = getAuthHeaders();
      if (!authHeaders) return;

      const response = await axios.get(
        `/api/trips/${tripId}/details`,
        authHeaders
      );
      
      setTripDetails(response.data);
    } catch (error) {
      console.error('Error fetching trip details:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        navigate('/login');
      } else {
        toast.error('Failed to fetch trip details');
      }
    } finally {
      setLoading(false);
    }
  }, [tripId, getAuthHeaders, navigate]);

  // Fetch trip details when modal opens
  useEffect(() => {
    if (isOpen && tripId) {
      fetchTripDetails();
    }
  }, [isOpen, tripId, fetchTripDetails]);

  const handleDelete = async () => {
    if (!tripDetails) return;
    
    setDeleting(true);
    try {
      const authHeaders = getAuthHeaders();
      if (!authHeaders) return;

      const deleteFlights = deleteOption === 'delete';
      
      await axios.delete(
        `/api/trips/${tripId}?deleteFlights=${deleteFlights}`,
        authHeaders
      );
      
      const successMessage = deleteFlights 
        ? `Trip "${tripNumber}" and all ${tripDetails.flight_count} flight(s) deleted successfully`
        : `Trip "${tripNumber}" deleted. ${tripDetails.flight_count} flight(s) moved to individual flights`;
        
      toast.success(successMessage);
      
      // Call the callback to refresh the flight list
      onTripDeleted();
      onClose();
      
    } catch (error) {
      console.error('Error deleting trip:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.error || 'Failed to delete trip');
      }
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Trip</h3>
              <p className="text-sm text-gray-500">Trip: {tripNumber}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : tripDetails ? (
            <>
              {/* Trip Info */}
              <div className="mb-6">
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Total Flights:</span>
                    <span className="text-sm font-semibold text-gray-900">{tripDetails.flight_count}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Completed:</span>
                    <span className="text-sm text-green-700">{tripDetails.completed_flights}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Incomplete:</span>
                    <span className="text-sm text-orange-700">{tripDetails.incomplete_flights}</span>
                  </div>
                  {tripDetails.first_flight_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">Date Range:</span>
                      <span className="text-sm text-gray-900">
                        {formatDate(tripDetails.first_flight_date)} - {formatDate(tripDetails.last_flight_date)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Delete Options */}
              <div className="mb-6">
                <p className="text-sm text-gray-700 mb-4">
                  What would you like to do with the {tripDetails.flight_count} flight(s) in this trip?
                </p>
                
                <div className="space-y-3">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="deleteOption"
                      value="unassign"
                      checked={deleteOption === 'unassign'}
                      onChange={(e) => setDeleteOption(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Keep flights as individual entries</div>
                      <div className="text-sm text-gray-600">
                        Delete only the trip. Flights will be moved to individual flights.
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="deleteOption"
                      value="delete"
                      checked={deleteOption === 'delete'}
                      onChange={(e) => setDeleteOption(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-red-700">Delete trip and all flights</div>
                      <div className="text-sm text-gray-600">
                        Permanently delete the trip and all {tripDetails.flight_count} flight(s). This cannot be undone.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Warning */}
              {deleteOption === 'delete' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Warning: Permanent Deletion</p>
                      <p className="text-sm text-red-700">
                        This will permanently delete all flight data including flight times, approaches, 
                        and remarks. This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Failed to load trip details</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex space-x-3 px-6 py-4 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting || loading || !tripDetails}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {deleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                <span>
                  {deleteOption === 'delete' ? 'Delete Trip & Flights' : 'Delete Trip Only'}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TripDeleteModal;